import { Grid, Segment } from '@mairistem/composants';
import { fetchModuleActif, Module, Produit } from '@mairistem/finances-utils';
import dayjs from 'dayjs';
import React from 'react';
import StorageKey from '../../constants/common/storage';
import ShowOnly from '../../constants/feuilleSaisie/showOnly';
import RecensementDemande from '../../constants/simulation/recensementDemande';
import TypeBudget from '../../constants/simulation/typeBudget';
import TypePeriode from '../../constants/simulation/typePeriode';
import type TreeRow from '../../interfaces/feuilleSaisie/treeRow';
import type Filters from '../../interfaces/simulation/filters';
import type Simulation from '../../interfaces/simulation/simulation';
import { getSimulation } from '../../services/simulation/simulation';
import { handleChangeMontantToSimulation } from '../../utils/simulation/simulation';
import SessionContext from '../common/Context/SessionContext';
import TreeView from '../feuilleSaisie/TreeView';
import BoiteOutil from './BoiteOutil';
import ElaborationBudgetPlaceholder from './ElaborationBudgetPlaceholder';
import Panels from './Panels/Panels';
import SimulationAction from './SimulationAction';

interface ElaborationBudgetProps {
	identifiantSimulation: number;
}

const ElaborationBudget = ({
	identifiantSimulation,
}: ElaborationBudgetProps) => {
	const { userRole } = React.useContext(SessionContext);
	const [filters, setFilters] = React.useState<Filters>();
	const [isDemandeEnabled, setIsDemandeEnabled] = React.useState(false);
	const [simulation, setSimulation] = React.useState<Simulation>(null);

	const getDefaultPeriode = (
		newSimulation: Simulation,
		hasDemande: boolean,
	) => {
		if (newSimulation.budget.type === TypeBudget.COMPTE_ADMINISTRATIF) {
			return TypePeriode.CA_CFU;
		}

		if (newSimulation.vote) return TypePeriode.VOTE;

		// Si on a le module des demandes et qu'on a le bon type de budget
		if (hasDemande) {
			const dateButoirDemande = dayjs(newSimulation.dateButoirDemande);
			const today = dayjs();

			// Si la date butoir n'est pas renseignee ou qu'elle n'est pas depassee
			if (
				!newSimulation.dateButoirDemande ||
				dateButoirDemande.isAfter(today) ||
				dateButoirDemande.isSame(today, 'day')
			) {
				return TypePeriode.DEMANDE;
			}
		}

		return TypePeriode.PROPOSE;
	};

	const isModuleDemandeEnabled = async (newSimulation: Simulation) => {
		// Optimisation, en CA_CFU, pas de gestion des demandes
		if (newSimulation.budget.type === TypeBudget.COMPTE_ADMINISTRATIF)
			return false;

		// On regarde si on a le module d'actif
		const moduleDemandeEnabled = await fetchModuleActif(
			StorageKey.MODULE_DEMANDE,
			Produit.ELABORATION_BUDGET,
			Module.ARBITRAGE_INSCRIPTIONS_BUDGETAIRES,
		);

		let shouldShowDemande = false;

		// On regarde si le type de budget correspond au parametrage de l'exercice
		switch (newSimulation.exercice.recensementDemande) {
			case RecensementDemande.BP:
				shouldShowDemande =
					newSimulation.budget.type === TypeBudget.BUDGET_PRIMITIF;
				break;
			case RecensementDemande.BP_BS:
				shouldShowDemande = [
					TypeBudget.BUDGET_PRIMITIF,
					TypeBudget.BUDGET_SUPPLEMENTAIRE,
				].includes(newSimulation.budget.type);
				break;
			case RecensementDemande.BP_BS_DM:
				shouldShowDemande = [
					TypeBudget.BUDGET_PRIMITIF,
					TypeBudget.BUDGET_SUPPLEMENTAIRE,
					TypeBudget.DECISION_MODIFICATIVE,
				].includes(newSimulation.budget.type);
				break;
			default:
				break;
		}

		shouldShowDemande = moduleDemandeEnabled && shouldShowDemande;

		setIsDemandeEnabled(shouldShowDemande);

		return shouldShowDemande;
	};

	const fetchSimulation = async (
		idSimulation: number,
		onlyPanelData: boolean,
		typePeriode: TypePeriode,
	) => {
		const s = await getSimulation(idSimulation);
		const moduleDemandeEnabled = await isModuleDemandeEnabled(s);

		// Remise a zero des filtres
		setFilters((old) => ({
			...old,
			showOnly: ShowOnly.TOUTES_LIGNES,
		}));

		if (onlyPanelData) {
			setSimulation((old) => ({
				...old,
				panelData: s.panelData,
			}));
		} else {
			setSimulation({
				...s,
				typePeriode: typePeriode ?? getDefaultPeriode(s, moduleDemandeEnabled),
			});
		}
	};

	const handleRefreshSimulation = (
		onlyPanelData = false,
		typePeriode: TypePeriode = null,
	) => {
		fetchSimulation(Number(identifiantSimulation), onlyPanelData, typePeriode);
	};

	// Chargement de la simulation
	// biome-ignore lint/correctness/useExhaustiveDependencies: pas besoin de 'handleRefreshSimulation'
	React.useEffect(() => {
		handleRefreshSimulation();
	}, []);

	const handleChangeMontant = (
		differencesMontants: any,
		imputation: TreeRow,
	) => {
		handleChangeMontantToSimulation(
			differencesMontants,
			imputation,
			simulation,
			setSimulation,
		);
	};

	const handleChangeSimulation = (data: Simulation) => {
		setSimulation((old) => ({ ...old, ...data }));
	};

	return simulation ? (
		<Segment basic secondary>
			<Grid stretched>
				<Grid.Row>
					<Grid.Column width={4}>
						<SimulationAction
							isDemandeEnabled={isDemandeEnabled}
							onRefresh={handleRefreshSimulation}
							onSave={handleChangeSimulation}
							simulation={simulation}
							userRole={userRole}
						/>
					</Grid.Column>
					<Grid.Column width={12}>
						<BoiteOutil
							onRefresh={handleRefreshSimulation}
							simulation={simulation}
							userRole={userRole}
						/>
					</Grid.Column>
				</Grid.Row>
			</Grid>

			<Panels simulation={simulation} />

			<TreeView
				filters={filters}
				onChangeMontant={handleChangeMontant}
				onRefresh={handleRefreshSimulation}
				setFilters={setFilters}
				simulation={simulation}
				userRole={userRole}
				isDemandeEnabled={isDemandeEnabled}
			/>
		</Segment>
	) : (
		<ElaborationBudgetPlaceholder />
	);
};

export default ElaborationBudget;
